import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, authStore, InitData, User } from '../../core'
import { t, intlLocale } from '../../core/i18n'
import { fmtDag, fmtTid, studietsTidszone } from '../../core/tidszone'

interface BookingData {
  id: string
  status: string
  bookingTime: string
  amountPaid: number
  checkedIn: boolean
  /** Opryknet plads, der venter på betaling (status 'pending'): frist og betalingsside. */
  paymentDeadline?: string | null
  payUrl?: string | null
  class: {
    id: string
    name: string
    startTime: string
    endTime: string
    room: string | null
    instructorName: string | null
  }
}

interface BookingsWidgetProps {
  initData: InitData
}

// Holdets dag og tid i studiets tidszone, ikke den besøgendes browser (QA 27-10).
function fmtDay(iso: string, locale: string, tz: string): string {
  return fmtDag(iso, locale, tz, { weekday: 'short', day: 'numeric', month: 'short' })
}
function fmtTime(iso: string, locale: string, tz: string): string {
  return fmtTid(iso, locale, tz)
}
function isUpcoming(iso: string): boolean {
  return new Date(iso) > new Date()
}

/**
 * BookingsWidget — mirrors HS-native styling.
 *
 * Card layout:
 *   - Date + time meta (uppercase, letter-spaced, muted, small)
 *   - Class name (bold, 1.15rem)
 *   - Room (small, muted)
 *   - Right side: status badge ("Bekræftet" / "Venteliste") + outlined red "Afmeld" button
 */
// `initData` is required by the abstract base class signature but this widget
// has no use for tenant branding/contact data — apiClient already has baseUrl
// configured during widget init, so all API calls work without it.
export function BookingsWidget({ initData: _initData }: BookingsWidgetProps) {
  const str = t(_initData.locale)
  const dateLocale = intlLocale(_initData.locale)
  const tz = studietsTidszone(_initData.timezone)
  const [bookings, setBookings] = useState<BookingData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(authStore.getUser())
  const [cancellingId, setCancellingId] = useState<string | null>(null)

  // Subscribe to auth changes
  useEffect(() => {
    return authStore.subscribe(setUser)
  }, [])

  const fetchBookings = useCallback(async () => {
    setLoading(true)
    setError(null)

    const result = await apiClient.get<BookingData[]>('/api/widget/bookings', true)

    if (result.error) {
      if (result.code === 'AUTH_REQUIRED') {
        // Token expired — clear local auth state so widget re-renders to logged-out
        authStore.clearTokens()
      } else {
        setError(result.error)
      }
    } else if (result.data) {
      setBookings(result.data)
    }

    setLoading(false)
  }, [])

  // Fetch bookings when user changes
  useEffect(() => {
    if (user) {
      fetchBookings()
    } else {
      setBookings([])
      setLoading(false)
    }
  }, [user, fetchBookings])

  async function handleCancel(booking: BookingData) {
    if (!confirm(str.confirmUnsubscribe.replace('{name}', booking.class.name))) {
      return
    }

    setCancellingId(booking.id)

    const result = await apiClient.delete(`/api/widget/bookings/${booking.id}`, true)

    setCancellingId(null)

    if (result.error) {
      alert(result.error)
    } else {
      await fetchBookings()
      window.dispatchEvent(new CustomEvent('holdstyring-widget-booking-cancelled', {
        detail: { bookingId: booking.id, className: booking.class.name }
      }))
    }
  }

  // Not logged in
  if (!user) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔐</div>
        <p style={{ margin: 0 }}>{str.loginToSeeBookings}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-bk-list">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-bk-card hsw-bk-skel-card">
              <div class="hsw-bk-card-main">
                <div class="hsw-skel hsw-bk-skel-meta" />
                <div class="hsw-skel hsw-bk-skel-name" />
                <div class="hsw-skel hsw-bk-skel-room" />
              </div>
              <div class="hsw-bk-card-side">
                <div class="hsw-skel hsw-bk-skel-badge" />
                <div class="hsw-skel hsw-bk-skel-btn" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-bk-error">
          <p>⚠️ {error}</p>
        </div>
      </div>
    )
  }

  // Filter to upcoming bookings
  const upcomingBookings = bookings.filter(b => isUpcoming(b.class.startTime))

  if (upcomingBookings.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📅</div>
        <p style={{ margin: 0 }}>{str.noUpcomingBookings}</p>
      </div>
    )
  }

  return (
    <div>
      <style>{styles}</style>
      <div class="hsw-bk-list">
        {upcomingBookings.map(booking => {
          const isConfirmed = booking.status === 'confirmed'
          const awaitingPayment = booking.status === 'pending' && !!booking.payUrl
          const isCancelling = cancellingId === booking.id
          return (
            <div key={booking.id} class="hsw-bk-card">
              <div class="hsw-bk-card-main">
                <div class="hsw-bk-meta">
                  {fmtDay(booking.class.startTime, dateLocale, tz)} · {fmtTime(booking.class.startTime, dateLocale, tz)}
                </div>
                <h3 class="hsw-bk-name">{booking.class.name}</h3>
                {booking.class.room && (
                  <div class="hsw-bk-room">{booking.class.room}</div>
                )}
                {awaitingPayment && booking.paymentDeadline && (
                  <div class="hsw-bk-room">
                    {str.payBy.replace('{time}', `${fmtDay(booking.paymentDeadline, dateLocale, tz)} · ${fmtTime(booking.paymentDeadline, dateLocale, tz)}`)}
                  </div>
                )}
              </div>
              <div class="hsw-bk-card-side">
                <span class={`hsw-bk-badge${isConfirmed ? ' hsw-bk-badge-confirmed' : ' hsw-bk-badge-waitlist'}`}>
                  {isConfirmed ? str.confirmed : awaitingPayment ? str.awaitingPayment : str.waitlist}
                </span>
                {awaitingPayment ? (
                  // Betalingen sker på studiets eget site (bag login), som i mailen.
                  <a class="hsw-bk-cancel hsw-bk-pay" href={booking.payUrl!} target="_blank" rel="noopener">
                    {str.payNow}
                  </a>
                ) : (
                <button
                  class="hsw-bk-cancel"
                  onClick={() => handleCancel(booking)}
                  disabled={isCancelling}
                  aria-label={isCancelling ? str.unsubscribe : undefined}
                >
                  {isCancelling ? '...' : str.unsubscribe}
                </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const styles = `
  .hsw-bk-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 900px;
    margin: 0 auto;
  }
  .hsw-bk-card {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2rem;
    transition: all .3s;
  }
  .hsw-bk-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }
  .hsw-bk-card-main {
    flex: 1;
    min-width: 0;
  }
  .hsw-bk-card-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    flex-shrink: 0;
  }
  .hsw-bk-meta {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--yoga-text-muted, #737373);
    font-weight: 600;
    margin-bottom: 0.4rem;
  }
  .hsw-bk-name {
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    margin: 0 0 0.35rem;
    line-height: 1.3;
  }
  .hsw-bk-room {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-bk-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 99px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .hsw-bk-badge-confirmed {
    background: rgba(255,107,53,0.12);
    color: var(--yoga-primary, #FF6B35);
  }
  .hsw-bk-badge-waitlist {
    background: #f3f4f6;
    color: #525252;
  }
  .hsw-bk-cancel {
    background: transparent;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: var(--yoga-radius, 0);
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: all .3s;
    min-width: 88px;
  }
  .hsw-bk-pay {
    text-decoration: none;
    text-align: center;
    color: var(--yoga-primary, #FF6B35) !important;
    border-color: currentColor !important;
  }
  .hsw-bk-cancel:hover:not(:disabled) {
    background: #dc2626;
    color: #fff;
    border-color: #dc2626;
  }
  .hsw-bk-cancel:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hsw-bk-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--yoga-radius, 0);
    padding: 1.25rem 1.5rem;
    max-width: 900px;
    margin: 0 auto;
  }
  .hsw-bk-error p {
    color: #dc2626;
    margin: 0;
    font-size: 0.9rem;
  }
  /* Skeletons */
  .hsw-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-bk-skel-card {
    pointer-events: none;
  }
  .hsw-bk-skel-meta { height: 12px; width: 40%; margin-bottom: 0.6rem; }
  .hsw-bk-skel-name { height: 20px; width: 65%; margin-bottom: 0.6rem; }
  .hsw-bk-skel-room { height: 14px; width: 30%; }
  .hsw-bk-skel-badge { height: 22px; width: 80px; }
  .hsw-bk-skel-btn { height: 34px; width: 88px; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (max-width: 540px) {
    .hsw-bk-card {
      flex-direction: column;
      gap: 1rem;
      padding: 1.5rem;
    }
    .hsw-bk-card-side {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }
  }
`
